import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";

// Helper: Format image source from MinIO or base64 or fallback
const getValidImage = (image) => {
    if (!image) return "https://via.placeholder.com/120";

    const cleanImage = image.replace(/\s/g, '');

    if (cleanImage.startsWith("data:image")) {
        return cleanImage;
    } else if (cleanImage.startsWith("catering/") || cleanImage.includes("/")) {
        // MinIO URL
        return `http://10.10.20.9:9000/${cleanImage}`;
    } else if (cleanImage.length > 100) {
        // Base64 data
        return `data:image/jpeg;base64,${cleanImage}`;
    } else {
        // Simple filename - assume it's in catering bucket
        return `http://10.10.20.9:9000/catering/${cleanImage}`;
    }
};

// Individual Team Card
const CateringLeadershipCardItem = ({ name, title, image, description }) => (
    <div style={cardStyle}>
        <img
            src={getValidImage(image)}
            alt={name || "Team Member"}
            style={imageStyle}
            onError={(e) => {
                e.target.src = "https://via.placeholder.com/120";
            }}
        />
        <h3 style={nameStyle}>{name || "Unnamed"}</h3>
        <p style={titleStyle}>{title || "No Title"}</p>
        <p style={descStyle}>{description || "No Description Available"}</p>
    </div>
);

// Main Component
const CateringLeadershipCard = () => {
    const [vendorId, setVendorId] = useState(null);
    const [teamData, setTeamData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /* 1️⃣ read vendorId once */
    useEffect(() => {
        setVendorId(localStorage.getItem("vendorId"));
    }, []);

    /* 2️⃣ fetch team list when vendorId is ready */
    useEffect(() => {
        if (!vendorId) return;

        setLoading(true);
        setError(null);

        const token = localStorage.getItem("authToken");

        // Using the catering API endpointy
        fetch(
            `http://10.10.20.9:7007/caterings-0.0.1-SNAPSHOT/api/vendor/team/getall/${vendorId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            }
        )
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((data) => {
                console.log("📦 Team API Response:", data);

                // Handle different response formats
                let teamArray = [];

                if (Array.isArray(data)) {
                    teamArray = data;
                } else if (data && Array.isArray(data.data)) {
                    teamArray = data.data;
                } else if (data && data.data) {
                    teamArray = Object.values(data.data);
                } else {
                    console.warn("Unexpected API format:", data);
                    teamArray = [];
                }

                console.log("🔄 Processed team data:", teamArray);

                if (teamArray.length === 0) {
                    setError("No team members found for this vendor");
                } else {
                    setTeamData(teamArray);
                }
            })
            .catch((err) => {
                console.error("❌ Error fetching team data:", err);
                setError("Failed to load team data: " + err.message);
            })
            .finally(() => setLoading(false));
    }, [vendorId]);

    return (
        <section style={sectionStyle} className="leadership-data-cards">
            <h1 style={headerTextStyle}>OUR TEAM</h1>

            {loading && <p style={{ textAlign: "center", color: "white" }}>Loading team members...</p>}
            {error && <p style={{ textAlign: "center", color: "yellow" }}>{error}</p>}

            {!loading && !error && teamData.length > 0 && (
                <Swiper
                    modules={[Autoplay]}
                    spaceBetween={20}
                    slidesPerView={1}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    breakpoints={{
                        640: { slidesPerView: 1 },
                        768: { slidesPerView: 2 },
                        1024: { slidesPerView: 3 }
                    }}
                >
                    {teamData.map((member, index) => (
                        <SwiperSlide key={member.teamId || member.id || index}>
                            <CateringLeadershipCardItem
                                name={member.name || member.fullName}
                                title={member.designation || member.role}
                                image={member.image || member.profilePicture}
                                description={member.description || member.bio}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            {!loading && !error && teamData.length === 0 && (
                <p style={{ textAlign: "center", color: "white" }}>
                    No team members available.
                </p>
            )}

            <br /><br />
        </section>
    );
};

// --- Inline Styles ---
const sectionStyle = {
    margin: "0 auto",
    borderRadius: "10px",
    padding: "20px",
    backgroundColor: 'rgb(177 93 198)',
};

const headerTextStyle = {
    fontSize: "32px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "28px",
    marginTop: "10px",
    color: "rgb(255 225 36)",
    fontFamily: 'revert'
};

const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "30px 20px",
    textAlign: "center",
    transition: "0.3s ease-in-out",
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
};

const imageStyle = {
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    objectFit: "cover",
    marginBottom: "20px",
    border: "4px solid #2a0947",
};

const nameStyle = {
    fontSize: "22px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
    textAlign: "center",
    wordWrap: "break-word",
    overflowWrap: "break-word",
};

const titleStyle = {
    fontSize: "16px",
    fontWeight: "500",
    color: "#ee6521",
    marginBottom: "12px",
    textAlign: "center",
    wordWrap: "break-word",
    overflowWrap: "break-word",
};

const descStyle = {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.5",
    textAlign: "center",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
};

export default CateringLeadershipCard;






